<template>
  <b-sidebar
    id="sidebar"
    :visible="isSidebarActive"
    bg-variant="white"
    sidebar-class="sidebar-lg"
    shadow
    backdrop
    no-header
    no-close-on-backdrop
    right
    @shown="loadData"
    @hidden="resetForm"
    @change="(val) => $emit('update:is-sidebar-active', val)"
  >
    <template #default="{ hide }">
      <!-- Header -->
      <div
        class="d-flex justify-content-between align-items-center content-sidebar-header px-2 py-1"
      >
        <h5 class="mb-0 text-capitalize">
          {{
            `${isAdd ? $t("actions.add") : $t("actions.edit")} ${$t(
              "device_tag.title"
            )}`
          }}
        </h5>

        <feather-icon class="ml-1 cursor-pointer" icon="XIcon" size="16" @click="hide" />
      </div>

      <!-- BODY -->
      <validation-observer #default="{ handleSubmit }" ref="refFormObserver">
        <!-- Form -->
        <b-form
          class="p-2"
          @submit.prevent="handleSubmit(onSubmit)"
          @reset.prevent="resetForm"
        >
          <validation-provider #default="validationContext" name="type" rules="required">
            <b-form-group
              :label="'Tenant'"
              label-for="type"
              :state="getValidationState(validationContext)"
            >
              <v-select
                v-model="itemData.id_tenant"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :options="listOptions"
                :clearable="false"
                :reduce="(val) => val.value"
                input-id="type"
              />
              <b-form-invalid-feedback :state="getValidationState(validationContext)">
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>

          <!-- Name -->
          <validation-provider #default="validationContext" name="name" rules="required">
            <b-form-group
              class="text-capitalize"
              :label="$t('plate_list.name')"
              label-for="name"
            >
              <b-form-input
                id="name"
                v-model="itemData.name"
                :state="getValidationState(validationContext)"
                trim
              />

              <b-form-invalid-feedback>
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>

          <!-- Icon Color -->
          <validation-provider #default="validationContext" name="icon_color">
            <b-form-group
              class="text-capitalize"
              :label="$t('types_alarms.color_icon')"
              label-for="icon_color"
            >
              <b-form-input
                id="icon_color"
                v-model="itemData.icon_color"
                :state="getValidationState(validationContext)"
                trim
                type="color"
              />

              <b-form-invalid-feedback>
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>

          <!-- Icon -->
          <validation-provider #default="validationContext" name="icon">
            <b-form-group
              class="text-capitalize"
              :label="$t('types_alarms.icon')"
              label-for="icon"
            >
              <!-- Icon Selector Button -->
              <div
                class="icon-selector-btn border rounded p-1 d-flex align-items-center justify-content-between cursor-pointer"
                @click="showIconPicker = !showIconPicker"
              >
                <div class="d-flex align-items-center">
                  <custom-icon
                    v-if="itemData.icon"
                    :name="itemData.icon"
                    size="28"
                    :color="itemData.icon_color"
                  />
                  <feather-icon v-else icon="ImageIcon" size="28" class="text-muted" />
                  <span class="ml-2">{{
                    itemData.icon || $t("types_alarms.no_icon")
                  }}</span>
                </div>
                <!-- Clear Icon Button -->
                <div>
                  <b-button
                    v-if="itemData.icon"
                    variant="outline-danger"
                    size="sm"
                    class="mr-1"
                    @click="clearIcon"
                  >
                    <feather-icon icon="XIcon" size="14" class="" />
                  </b-button>
                  <feather-icon
                    :icon="showIconPicker ? 'ChevronUpIcon' : 'ChevronDownIcon'"
                    size="18"
                  />
                </div>
              </div>

              <!-- Icon Picker Dropdown -->
              <div v-if="showIconPicker" class="icon-picker-dropdown mt-2">
                <!-- Search filter -->
                <b-form-input
                  v-model="iconSearch"
                  :placeholder="$t('table.search') + '...'"
                  class="mb-2"
                  trim
                />

                <!-- Icon Grid -->
                <div class="icon-grid-container border rounded p-2">
                  <div class="icon-grid">
                    <div
                      v-for="icon in filteredIcons"
                      :key="icon"
                      class="icon-grid-item"
                      :class="{ selected: itemData.icon === icon }"
                      @click="selectIcon(icon)"
                      :title="icon"
                    >
                      <custom-icon :name="icon" size="24" :color="itemData.icon_color" />
                    </div>
                  </div>
                  <div
                    v-if="filteredIcons.length === 0"
                    class="text-center text-muted py-2"
                  >
                    {{ $t("no_record_found") }}
                  </div>
                </div>
              </div>

              <b-form-invalid-feedback :state="getValidationState(validationContext)">
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>

          <!-- Form Actions -->
          <div class="d-flex mt-2">
            <b-button
              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
              variant="primary"
              class="mr-2 text-capitalize"
              type="submit"
            >
              {{ isAdd ? $t("actions.add") : $t("actions.edit") }}
            </b-button>
            <b-button
              v-ripple.400="'rgba(186, 191, 199, 0.15)'"
              type="button"
              variant="outline-secondary"
              @click="hide"
              class="text-capitalize"
            >
              {{ $t("actions.cancel") }}
            </b-button>
          </div>
        </b-form>
      </validation-observer>
    </template>
  </b-sidebar>
</template>

<script>
import {
  BSidebar,
  BForm,
  BFormGroup,
  BFormInput,
  BFormInvalidFeedback,
  BButton,
  BFormCheckbox,
} from "bootstrap-vue";
import { ValidationProvider, ValidationObserver } from "vee-validate";
import vSelect from "vue-select";
import { ref, computed } from "@vue/composition-api";
import { required, alphaNum, email, positive, required_if } from "@validations";
import { useToast } from "vue-toastification/composition";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import formValidation from "@core/comp-functions/forms/form-validation";
import Ripple from "vue-ripple-directive";
import store from "@/store";
import axios from "@axios";
import i18n from "@/libs/i18n";
import { getUserErrorMessage } from "@/utils/responseErrors";
import { ICON_NAMES } from "@/assets/svg/CustomIcons";

export default {
  components: {
    BSidebar,
    BForm,
    BFormGroup,
    BFormInput,
    BFormInvalidFeedback,
    BButton,
    vSelect,
    BFormCheckbox,

    // Form Validation
    ValidationProvider,
    ValidationObserver,
  },
  directives: {
    Ripple,
  },
  model: {
    prop: "isSidebarActive",
    event: "update:is-sidebar-active",
  },
  props: {
    isSidebarActive: {
      type: Boolean,
      required: true,
    },
    itemEdit: {
      type: Object,
      required: false,
    },
    listOptions: {
      type: Array,
      required: true,
    },
    isAdd: {
      type: Boolean,
      required: true,
    },
  },
  data() {
    return {
      required,
      alphaNum,
      email,
      positive,
    };
  },
  setup(props, { emit }) {
    const blankData = {
      name: "",
      id_tenant: null,
      icon_color: "",
      icon: "",
    };

    const iconOptions = ICON_NAMES;
    const iconSearch = ref("");
    const showIconPicker = ref(false);

    const filteredIcons = computed(() => {
      if (!iconSearch.value) return iconOptions;
      const search = iconSearch.value.toLowerCase();
      return iconOptions.filter((name) => name.toLowerCase().includes(search));
    });

    const toast = useToast();

    const itemData = ref(JSON.parse(JSON.stringify(blankData)));
    const resetData = () => {
      itemData.value = JSON.parse(JSON.stringify(blankData));
    };

    const onSubmit = () => {
      let dispatch;

      if (props.isAdd) {
        dispatch = axios.post(
          `v1/tenants/device-tags/${itemData.value.id_tenant}`,
          { ...itemData.value }
        );
      } else {
        const req = {
          id: itemData.value.id,
          params: {
            name: itemData.value.name,
            id_tenant: itemData.value.id_tenant,
            icon_color: itemData.value.icon_color,
            icon: itemData.value.icon,
          },
        };

        dispatch = axios.put(`v1/tenants/device-tags/${req.id}`, {
          ...req.params,
        });
      }

      dispatch
        .then(({ data }) => {
          if (data.success) {
            toast({
              component: ToastificationContent,
              props: {
                title: data.message,
                icon: "CheckIcon",
                variant: "success",
              },
            });
            emit("refetch-data");
            emit("update:is-sidebar-active", false);
          } else {
            toast({
              component: ToastificationContent,
              props: {
                title: data.message,
                icon: "AlertTriangleIcon",
                variant: "danger",
              },
            });
          }
        })
        .catch((response) => {
          toast({
            component: ToastificationContent,
            props: {
              title: getUserErrorMessage(response, i18n.t("messages.errors.general")),
              icon: "AlertTriangleIcon",
              variant: "danger",
            },
          });
        });
    };

    const { refFormObserver, getValidationState, resetForm } = formValidation(resetData);

    const loadData = () => {
      if (!props.isAdd) {
        itemData.value = { ...blankData, ...props.itemEdit };
      }
    };

    const selectIcon = (icon) => {
      itemData.value.icon = icon;
      showIconPicker.value = false;
      iconSearch.value = "";
    };

    const clearIcon = () => {
      itemData.value.icon = "";
      showIconPicker.value = false;
      iconSearch.value = "";
    };

    return {
      itemData,
      onSubmit,

      refFormObserver,
      getValidationState,
      loadData,
      resetForm,
      iconOptions,
      iconSearch,
      filteredIcons,
      showIconPicker,
      selectIcon,
      clearIcon,
    };
  },
};
</script>

<style scoped>
.icon-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(44px, 1fr));
  gap: 8px;
}

.icon-grid:before {
  content: unset;
}

.icon-grid-item {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
}

/* --info rather than --primary: the sidebar turns --third-dark (#0e1e2d) in dark mode,
   where --primary (#1b3d5e) reads as invisible. The fallback is required because the
   app colors are injected at runtime (store/psim-config setAppColors) and a user
   without a saved theme has no --info at all. */
.icon-grid-item.selected {
  border-color: var(--info, #3e8cc7);
  box-shadow: 0 0 0 1px var(--info, #3e8cc7);
}

.icon-grid-container::-webkit-scrollbar {
  width: 6px;
}

.icon-grid-container::-webkit-scrollbar-thumb {
  background-color: #ccc;
  border-radius: 3px;
}

.icon-grid-container::-webkit-scrollbar-thumb:hover {
  background-color: #999;
}

.icon-selector-btn {
  transition: all 0.2s ease;
}

.icon-grid-container {
  max-height: 250px;
  overflow-y: auto;
}

.cursor-pointer {
  cursor: pointer;
}
</style>